/**
 * Queue tilt (queueTilt.ts), ES / MES charts only, two parts:
 *
 * - Ribbon (QueueTiltRibbonPrimitive): one cell per displayed bar along the
 *   bottom of the price pane, the bar's mean tilt; on MES a thinner ES row
 *   sits above MES's own. History as far back as the lines store holds it.
 * - Band: a thin strip at the right edge of the price pane, just left of the
 *   price scale, with the live value ("▲ 0.62"); on MES also ES's read and
 *   whether it agrees. Hidden while the newest point is missing or older than
 *   QUEUE_TILT_STALE_MS (e.g. before the service publishes the line at all).
 *
 * Lines are watched per root (flowLinesStore counts watches per root, so an ES
 * chart and an MES chart share one `flow-lines:ES` room). The band is a DOM
 * overlay so a headless check can read it: data-testid="queue-tilt-band",
 * data-tilt (3 decimals), data-root, data-strong, data-es-tilt, data-agree,
 * data-ribbon-cells.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { IChartApi, ISeriesApi, SeriesType, Time } from 'lightweight-charts';
import { useFlowLinesStore, type FlowLinePoint } from '../../store/flowLinesStore';
import { normalizeToBase } from '../../utils/symbolUtils';
import {
  QUEUE_TILT_LINE,
  latestQueueTilt,
  queueTiltRoots,
  queueTiltStyle,
  tiltAgreement,
  tiltCells,
  type QueueTiltRoot,
} from './queueTilt';
import {
  QueueTiltRibbonPrimitive,
  RIBBON_PRIMARY_COMPACT_PX,
  RIBBON_PRIMARY_PX,
  RIBBON_SECONDARY_PX,
  type RibbonRow,
} from './QueueTiltRibbonPrimitive';

/** Band width, px. */
export const QUEUE_TILT_BAND_PX = 8;
/** How often the band re-checks staleness, the pane geometry and the series, ms. */
const TICK_MS = 1_000;

interface Geometry {
  /** Price scale width: the band's right offset, px. */
  right: number;
  /** Price pane height (excludes the time axis and any line panes), px. */
  height: number;
}

const sameGeometry = (a: Geometry | null, b: Geometry | null): boolean =>
  a === b || (!!a && !!b && a.right === b.right && a.height === b.height);

function measure(chart: IChartApi | null): Geometry | null {
  if (!chart) return null;
  try {
    const right = chart.priceScale('right').width();
    const height = chart.panes()[0]?.getHeight() ?? 0;
    if (!(height > 0)) return null;
    return { right, height };
  } catch {
    return null; // chart removed under us
  }
}

const NO_POINTS: readonly FlowLinePoint[] = [];

/** Watches `root`'s queue_tilt line while `on` (the chart's own spelling when it is that root). */
function useTiltLine(symbol: string, root: QueueTiltRoot | null, on: boolean): readonly FlowLinePoint[] {
  const watchSymbol = root !== null && normalizeToBase(symbol) === root ? symbol : root;
  useEffect(() => {
    if (!on || !watchSymbol) return;
    const store = useFlowLinesStore.getState();
    store.initialize();
    const unwatch = store.watchLines(watchSymbol, [QUEUE_TILT_LINE]);
    return () => {
      unwatch();
      useFlowLinesStore.getState().teardown();
    };
  }, [on, watchSymbol]);
  return useFlowLinesStore((s) => (on && root ? s.byRoot[root]?.[QUEUE_TILT_LINE] ?? NO_POINTS : NO_POINTS));
}

type AnySeries = ISeriesApi<SeriesType, Time>;

export const QueueTiltBand: React.FC<{
  getChart: () => IChartApi | null;
  /** The candle series the ribbon attaches to (null until the chart exists). */
  getSeries?: () => AnySeries | null;
  symbol: string;
  /** Master switch && flowQueueTilt (Clean already applied). */
  enabled: boolean;
  colors: { high: string; low: string };
  /** Displayed timeframe, minutes (ribbon cells are per bar). */
  tfMinutes?: number;
  /** Phone / narrow chart: a slightly thinner ribbon. */
  compact?: boolean;
}> = ({ getChart, getSeries, symbol, enabled, colors, tfMinutes = 1, compact = false }) => {
  const roots = queueTiltRoots(symbol);
  const active = enabled && roots !== null;
  const primaryRoot = roots?.primary ?? null;
  const secondaryRoot = roots?.secondary ?? null;
  const points = useTiltLine(symbol, primaryRoot, active);
  const esPoints = useTiltLine(symbol, secondaryRoot, active && secondaryRoot !== null);

  const [now, setNow] = useState(() => Date.now());
  const [geo, setGeo] = useState<Geometry | null>(null);
  const ribbonRef = useRef<QueueTiltRibbonPrimitive | null>(null);
  const attachedTo = useRef<AnySeries | null>(null);
  const [series, setSeries] = useState<AnySeries | null>(null);

  // Geometry, staleness and the series the ribbon belongs on (the chart is
  // created by the parent's effects, after this child's first effects).
  useEffect(() => {
    if (!active) return;
    const tick = () => {
      setNow(Date.now());
      const g = measure(getChart());
      setGeo((prev) => (sameGeometry(prev, g) ? prev : g));
      setSeries(getSeries?.() ?? null);
    };
    tick();
    const id = setInterval(tick, TICK_MS);
    return () => clearInterval(id);
  }, [active, getChart, getSeries]);

  // Attach the ribbon to the current series; move it when the series changes.
  useEffect(() => {
    if (!active || !series) return;
    const prim = ribbonRef.current ?? new QueueTiltRibbonPrimitive();
    ribbonRef.current = prim;
    try {
      series.attachPrimitive(prim);
      attachedTo.current = series;
    } catch {
      return;
    }
    return () => {
      try {
        attachedTo.current?.detachPrimitive(prim);
      } catch {
        // chart already removed
      }
      attachedTo.current = null;
    };
  }, [active, series]);

  const primaryCells = useMemo(() => tiltCells(points, tfMinutes), [points, tfMinutes]);
  const esCells = useMemo(() => (secondaryRoot ? tiltCells(esPoints, tfMinutes) : []), [esPoints, secondaryRoot, tfMinutes]);
  useEffect(() => {
    const prim = ribbonRef.current;
    if (!prim || !active) return;
    const rows: RibbonRow[] = [{ cells: primaryCells, height: compact ? RIBBON_PRIMARY_COMPACT_PX : RIBBON_PRIMARY_PX }];
    if (secondaryRoot) rows.push({ cells: esCells, height: RIBBON_SECONDARY_PX });
    prim.setData(rows, colors);
  }, [active, series, primaryCells, esCells, secondaryRoot, colors, compact]);

  if (!active || !geo) return null;
  // A point newer than the last tick is fresh by definition.
  const latestTs = Math.max(points.length > 0 ? points[points.length - 1][0] : 0, esPoints.length > 0 ? esPoints[esPoints.length - 1][0] : 0);
  const clock = Math.max(now, latestTs);
  const reading = latestQueueTilt(points, clock);
  const esReading = secondaryRoot ? latestQueueTilt(esPoints, clock) : null;
  const cells = primaryCells.length + esCells.length;
  if (!reading) {
    // No live value: keep only the (invisible) hook so a check can see the ribbon's state.
    return <div data-testid="queue-tilt-ribbon" data-ribbon-cells={cells} style={{ display: 'none' }} />;
  }
  const st = queueTiltStyle(reading.value, colors);
  const es = esReading ? queueTiltStyle(esReading.value, colors) : null;
  const agree = tiltAgreement(reading.value, esReading?.value ?? null);
  const side = (v: number) => (v > 0 ? 'bid' : v < 0 ? 'ask' : 'no');
  const title = `Queue tilt (${primaryRoot}) ${st.label}: ${side(reading.value)} queue heavier${st.strong ? ' (strong)' : ''}.`
    + (es && esReading ? ` ES book ${es.label}${agree ? `, ${agree}` : ''}.` : '')
    + ` 2024-2025: next 5 s went the tilt's way ${primaryRoot === 'ES' ? '67%' : '66%'} of the time`
    + `${secondaryRoot ? " (ES's book on MES 61%)" : ''}. Context, not a signal.`;
  return (
    <div
      className="queue-tilt-band"
      data-testid="queue-tilt-band"
      data-tilt={reading.value.toFixed(3)}
      data-root={primaryRoot ?? ''}
      data-strong={st.strong ? 'true' : 'false'}
      data-es-tilt={esReading ? esReading.value.toFixed(3) : ''}
      data-agree={agree ?? ''}
      data-ribbon-cells={cells}
      title={title}
      style={{
        position: 'absolute',
        top: 0,
        right: geo.right,
        height: geo.height,
        width: QUEUE_TILT_BAND_PX,
        pointerEvents: 'none',
        zIndex: 3,
      }}
    >
      <div style={{ position: 'absolute', inset: 0, background: st.color, opacity: st.opacity }} />
      <span
        className="queue-tilt-label"
        data-testid="queue-tilt-label"
        style={{
          position: 'absolute',
          right: QUEUE_TILT_BAND_PX + 2,
          // clear the ribbon rows at the bottom of the pane
          bottom: secondaryRoot ? 14 : 10,
          fontSize: 10,
          lineHeight: '12px',
          whiteSpace: 'nowrap',
          color: st.color,
          opacity: Math.max(0.55, st.opacity),
          fontVariantNumeric: 'tabular-nums',
          textAlign: 'right',
        }}
      >
        {secondaryRoot ? `${primaryRoot} ` : ''}{st.label}
        {es && (
          <span data-testid="queue-tilt-es" style={{ display: 'block', color: es.color, opacity: Math.max(0.55, es.opacity) }}>
            ES {es.label}{agree === 'agrees' ? ' ✓' : agree === 'disagrees' ? ' ✗' : ''}
          </span>
        )}
      </span>
    </div>
  );
};

export default QueueTiltBand;
